import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import EarningsBreakdown from './EarningsBreakdown';
import SupportModal from './SupportModal';
import EmergencyCallButton from './EmergencyCallButton';
import PaymentModeIndicator from './PaymentModeIndicator';
import { 
  Truck, 
  Plus, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Users, 
  DollarSign,
  LogOut,
  Settings,
  Bell,
  BellOff,
  ToggleLeft,
  ToggleRight,
  HelpCircle,
  MessageSquare,
  MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const PAYMENT_MODE = stripeKey.startsWith('pk_live_') ? 'live' : 'test';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showEarningsModal, setShowEarningsModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch jobs for user-specific stats
      const jobsResponse = await axios.get(`${API}/jobs`);
      const jobs = jobsResponse.data;
      
      // Calculate user-specific stats
      let userStats = {};
      
      if (user.is_admin) {
        // For admin, fetch admin dashboard data
        try {
          const adminResponse = await axios.get(`${API}/admin/dashboard`);
          userStats = adminResponse.data;
        } catch (error) {
          console.error('Failed to fetch admin stats:', error);
        }
      } else {
        // For regular users with capabilities
        const myPostedJobs = jobs.filter(job => job.originator_id === user.id);
        const myAcceptedJobs = jobs.filter(job => job.accepted_by === user.id);
        const availableJobs = jobs.filter(job => 
          job.status === 'posted' && 
          job.originator_id !== user.id
        );

        // Real earnings: sum of receiver_amount for completed jobs this user accepted
        const myCompletedAccepted = myAcceptedJobs.filter(job => job.status === 'completed');
        const totalEarnings = myCompletedAccepted.reduce(
          (sum, job) => sum + (job.receiver_amount || 0), 0
        );
        
        userStats = {
          posted_jobs: myPostedJobs.length,
          accepted_jobs: myAcceptedJobs.length,
          completed_jobs: [...myPostedJobs, ...myAcceptedJobs].filter(job => job.status === 'completed').length,
          available_jobs: availableJobs.length,
          active_jobs: [...myPostedJobs, ...myAcceptedJobs].filter(job => 
            ['posted', 'accepted'].includes(job.status)
          ).length,
          total_earnings: totalEarnings
        };
      }
      
      setStats(userStats);
      setRecentJobs(jobs.slice(0, 5)); // Show 5 most recent jobs
      
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleOnCall = async () => {
    try {
      const newOnCallStatus = !profile.is_on_call;
      await axios.patch(`${API}/profile`, {
        is_on_call: newOnCallStatus
      });
      
      setProfile({ ...profile, is_on_call: newOnCallStatus });
      toast.success(newOnCallStatus ? 'You are now on-call for job alerts!' : 'You are now off-call. No job alerts will be sent.');
    } catch (error) {
      console.error('Failed to update on-call status:', error);
      toast.error('Failed to update on-call status');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="jop-gradient p-2 rounded-lg">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <h1 className="ml-3 text-xl font-bold text-gray-900">JOP Dashboard</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, <strong>{user.business_name}</strong>
              </span>
              
              {/* Payment Mode Indicator - Always visible */}
              <PaymentModeIndicator mode={PAYMENT_MODE} size="sm" />
              
              {/* On-call toggle for users who can receive jobs */}
              {profile && user.can_receive_jobs && !user.is_admin && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={toggleOnCall}
                    className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      profile.is_on_call
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {profile.is_on_call ? (
                      <>
                        <Bell className="h-4 w-4" />
                        <span>On-call</span>
                      </>
                    ) : (
                      <>
                        <BellOff className="h-4 w-4" />
                        <span>Off-call</span>
                      </>
                    )}
                  </button>
                </div>
              )}
              
              {user.is_admin && (
                <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                  Admin
                </span>
              )}
              
              <button
                onClick={handleLogout}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Capability badges */}
        {!user.is_admin && (
          <div className="mb-6 flex space-x-2">
            {user.can_post_jobs && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                Can Post Jobs
              </span>
            )}
            {user.can_receive_jobs && (
              <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                Can Receive Jobs
              </span>
            )}
            {profile && user.can_receive_jobs && (
              <span className={`px-3 py-1 text-sm rounded-full ${
                profile.is_on_call 
                  ? 'bg-emerald-100 text-emerald-800' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {profile.is_on_call ? 'On-call for alerts' : 'Off-call'}
              </span>
            )}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {user.is_admin ? (
            <>
              <button
                onClick={() => navigate('/jobs-list?status=all')}
                className="jop-card p-6 hover:shadow-lg transition-all duration-200 text-left w-full"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">{stats.total_jobs || 0}</h3>
                    <p className="text-sm text-gray-600">Total Jobs</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => navigate('/jobs-list?status=completed')}
                className="jop-card p-6 hover:shadow-lg transition-all duration-200 text-left w-full"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">{stats.completed_jobs || 0}</h3>
                    <p className="text-sm text-gray-600">Completed</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => navigate('/users')}
                className="jop-card p-6 hover:shadow-lg transition-all duration-200 text-left w-full"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">{stats.total_users || 0}</h3>
                    <p className="text-sm text-gray-600">Total Users</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => navigate('/admin')}
                className="jop-card p-6 hover:shadow-lg transition-all duration-200 text-left w-full"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <DollarSign className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      ${(stats.total_platform_commission || 0).toFixed(0)}
                    </h3>
                    <p className="text-sm text-gray-600">Platform Revenue</p>
                  </div>
                </div>
              </button>
            </>
          ) : (
            <>
              {user.can_post_jobs && (
                <button
                  onClick={() => navigate(`/jobs-list?ownership=originated`)}
                  className="jop-card p-6 hover:shadow-lg transition-all duration-200 text-left w-full"
                >
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Plus className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">{stats.posted_jobs || 0}</h3>
                      <p className="text-sm text-gray-600">Jobs Posted</p>
                    </div>
                  </div>
                </button>
              )}
              
              {user.can_receive_jobs && (
                <>
                  <button
                    onClick={() => navigate('/jobs-list?status=posted')}
                    className="jop-card p-6 hover:shadow-lg transition-all duration-200 text-left w-full"
                  >
                    <div className="flex items-center">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Bell className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900">{stats.available_jobs || 0}</h3>
                        <p className="text-sm text-gray-600">Available Jobs</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => navigate(`/jobs-list?ownership=received`)}
                    className="jop-card p-6 hover:shadow-lg transition-all duration-200 text-left w-full"
                  >
                    <div className="flex items-center">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <Clock className="h-6 w-6 text-yellow-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900">{stats.accepted_jobs || 0}</h3>
                        <p className="text-sm text-gray-600">Jobs Accepted</p>
                      </div>
                    </div>
                  </button>
                </>
              )}
              
              <button
                onClick={() => navigate(`/jobs-list?status=completed&ownership=received`)}
                className="jop-card p-6 hover:shadow-lg transition-all duration-200 text-left w-full"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">{stats.completed_jobs || 0}</h3>
                    <p className="text-sm text-gray-600">Completed Jobs</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => setShowEarningsModal(true)}
                className="jop-card p-6 hover:shadow-lg transition-all duration-200 text-left w-full"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <DollarSign className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      ${(stats.total_earnings || 0).toFixed(2)}
                    </h3>
                    <p className="text-sm text-gray-600">Earnings</p>
                  </div>
                </div>
              </button>
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="jop-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              
              {/* Emergency Breakdown - Full width prominent button */}
              <div className="mb-6">
                <EmergencyCallButton variant="prominent" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <Link
                  to="/jobs-list"
                  className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Clock className="h-8 w-8 text-blue-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">View Jobs</h4>
                    <p className="text-sm text-gray-600">Browse available jobs</p>
                  </div>
                </Link>
                
                {user.is_admin && (
                  <button
                    onClick={() => navigate('/admin')}
                    className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors w-full text-left"
                  >
                    <Settings className="h-8 w-8 text-purple-600 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">Admin Panel</h4>
                      <p className="text-sm text-gray-600">Manage users & jobs</p>
                    </div>
                  </button>
                )}
                
                {user.can_post_jobs && (
                  <Link
                    to="/jobs?create=true"
                    className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Plus className="h-8 w-8 text-green-600 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">Post Job</h4>
                      <p className="text-sm text-gray-600">Create new overflow job</p>
                    </div>
                  </Link>
                )}
                
                {profile && user.can_receive_jobs && (
                  <button
                    onClick={toggleOnCall}
                    className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                  >
                    {profile.is_on_call ? (
                      <BellOff className="h-8 w-8 text-red-600 mr-3" />
                    ) : (
                      <Bell className="h-8 w-8 text-green-600 mr-3" />
                    )}
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {profile.is_on_call ? 'Go Off-call' : 'Go On-call'}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {profile.is_on_call ? 'Stop receiving alerts' : 'Start receiving alerts'}
                      </p>
                    </div>
                  </button>
                )}
                
                {/* Support - Always visible */}
                <button
                  onClick={() => setShowSupportModal(true)}
                  className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                >
                  <HelpCircle className="h-8 w-8 text-orange-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Support</h4>
                    <p className="text-sm text-gray-600">Contact JOP Support team</p>
                  </div>
                </button>
                
                {/* My Requests - Always visible for logged-in users */}
                <button
                  onClick={() => navigate('/support/requests')}
                  className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                >
                  <MessageSquare className="h-8 w-8 text-blue-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">My Requests</h4>
                    <p className="text-sm text-gray-600">View your support tickets</p>
                  </div>
                </button>
                
                {/* Business Location - For dealers */}
                {user.can_receive_jobs && (
                  <button
                    onClick={() => navigate('/business/location')}
                    className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                  >
                    <MapPin className="h-8 w-8 text-purple-600 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">Business Location</h4>
                      <p className="text-sm text-gray-600">Configure location for emergency routing</p>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
          
          <div>
            <div className="jop-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {recentJobs.length > 0 ? (
                  recentJobs.map((job) => (
                    <button 
                      key={job.id} 
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      className="flex items-center p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors cursor-pointer w-full text-left"
                    >
                      <div className={`w-3 h-3 rounded-full mr-3 ${
                        job.status === 'completed' ? 'bg-green-500' :
                        job.status === 'accepted' ? 'bg-yellow-500' :
                        job.status === 'posted' ? 'bg-blue-500' : 'bg-red-500'
                      }`}></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{job.title}</p>
                        <p className="text-xs text-gray-600">{job.location}</p>
                      </div>
                      <div className="text-xs text-gray-400">
                        {job.status === 'completed' ? 'Completed' :
                         job.status === 'accepted' ? 'Assigned' :
                         job.status === 'posted' ? 'Live' : 
                         job.status === 'passed_back' ? 'Reassigned' : 'Unknown'}
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-600">No recent activity</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Earnings Modal */}
      <EarningsBreakdown 
        isOpen={showEarningsModal} 
        onClose={() => setShowEarningsModal(false)} 
      />
      
      {/* Support Modal */}
      <SupportModal 
        isOpen={showSupportModal} 
        onClose={() => setShowSupportModal(false)} 
      />
    </div>
  );
};

export default Dashboard;
